/**
 * Shared SummaryCard for stitch review pages — issue #974 review.
 *
 * Originally lived inline in StitchReviewPage. ClickStitchReviewPage
 * (#963 / #972) forked it and drifted. The fork was the reviewer's
 * Critical finding — same product surface, two different chip looks.
 * Extracting here gives both pages one source of truth for the
 * filter-chip styling and adds two extra tones (`blue` for user
 * stitches, `purple` for AI proposals) that the click page needs
 * but the url page hadn't.
 *
 * #999: `tooltip` slot for a definition of the bucket. Hovers reveal
 * an InfoTooltip popover with multi-line copy explaining what the
 * card counts and how items land in it.
 *
 * Visual contract:
 *   - p-4, bg-slate-50/dark:slate-800, rounded border
 *   - active: border-primary + ring-2 ring-primary/30
 *   - hover: border-primary/50 + shadow-md
 *   - testid: `card-${tone}` (matches URL page's existing convention)
 *   - label: uppercase xs slate
 *   - value: text-2xl font-semibold, tone-coloured
 */

import type { ReactNode } from 'react';
import { InfoTooltip } from '../ui/InfoTooltip';

export type SummaryCardTone = 'green' | 'amber' | 'red' | 'slate' | 'blue' | 'purple';

const valueToneClass: Record<SummaryCardTone, string> = {
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-red-600 dark:text-red-400',
  slate: 'text-slate-500 dark:text-slate-400',
  blue: 'text-blue-600 dark:text-blue-400',
  purple: 'text-purple-600 dark:text-purple-400',
};

export function SummaryCard({
  label,
  value,
  tone,
  active,
  onClick,
  hint,
  tooltip,
  tooltipAlign,
}: {
  label: string;
  value: number;
  tone: SummaryCardTone;
  active?: boolean;
  onClick?: () => void;
  /**
   * Optional second-line context shown under the value. Used by the
   * click page to clarify the auto-confidence boundary on the
   * "In effect" / "Candidates" chips. URL page doesn't use this
   * field — the existing 4-tone vocabulary is unambiguous there.
   */
  hint?: string;
  /**
   * #999: optional rich tooltip explaining what the card counts and
   * how items land in it. Rendered next to the label via InfoTooltip.
   */
  tooltip?: ReactNode;
  /**
   * #1001 review: right-most cards in a grid need the popover to
   * anchor by its right edge to avoid viewport overflow. Defaults to
   * left-anchored (icon's left edge).
   */
  tooltipAlign?: 'left' | 'right' | 'center';
}) {
  return (
    <button
      data-testid={`card-${tone}`}
      onClick={onClick}
      className={`p-4 bg-slate-50 dark:bg-slate-800 rounded border text-left transition-all cursor-pointer hover:shadow-md ${
        active
          ? 'border-primary ring-2 ring-primary/30'
          : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs text-slate-500 uppercase">
        <span>{label}</span>
        {tooltip && (
          <span
            onClick={(e) => e.stopPropagation()}
            className="normal-case"
          >
            <InfoTooltip align={tooltipAlign}>{tooltip}</InfoTooltip>
          </span>
        )}
      </div>
      <div className={`text-2xl font-semibold ${valueToneClass[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </button>
  );
}
